import React from 'react';
import {
  getGridStringOperators,
  DEFAULT_GRID_COL_TYPE_KEY,
  getGridDefaultColumnTypes,
  getGridDateOperators,
  getGridSingleSelectOperators,
  GridFilterItem,
  GridFilterInputValueProps,
  GridFilterInputValue,
} from '@mui/x-data-grid-premium';
import { fieldValidator } from '../../utils';
import StyledTooltip from '../StyledTooltip';
import RenderEditField from './RenderEditField';
import dayjs from 'dayjs';
import RenderHeaderDateFilterField from './RenderHeaderDateFilterField';
import style from './SetColumnConfig.module.css';
import { Chip, Tooltip, Checkbox, IconButton } from '@airbus/components-react';
import RenderHeaderMultiSelectFilterField from './RenderHeaderMultiSelectFilterField';
import { CalendarToday, UnfoldMore } from '@airbus/icons/react';
import { AutocompleteInput } from './AutocompleteInput';
import { PartialGridFilterItem } from '../../types';
import CellWithTooltipCheck from '../CellWithTooltipCheck';

export default function SetColumnConfig(props) {
  const { allColumns, access, allDropdowns, language } = props;
  const accessibleColumns = allColumns
    ?.filter((i) => access[`${i.attrInternalName}`]?.accessLevel !== 'H')
    .map((item) => ({
      ...item,
      editable: access[`${item.attrInternalName}`]?.accessLevel === 'U',
      sortable: !!access[`sorting`],
    }));

  const columnsData = accessibleColumns?.map((res) => {
    const { attrInternalName, attrDataType, mandatoryColumn, attrMinWidth } = res;

    const { headerName, label } = getHeaderName(res, mandatoryColumn, language);

    const column = {
      ...res,
      headerName,
      label,
      field: attrInternalName,
      type: attrDataType,
      required: mandatoryColumn,
      headerAlign: 'left',

      //   1 ch space = 8px / 16px
      ...(attrMinWidth && { minWidth: attrMinWidth * 8 }),
    };
    const columnObj = getColumnObj(column, language);
    if (attrDataType === 'date') {
      const dateColumnObj = getDateColumnObj();
      Object.assign(columnObj, dateColumnObj);
    } else if (attrDataType === 'singleSelect') {
      const singleSelectColumnObj = getSingleSelectColumnObj(allDropdowns[attrInternalName]);
      Object.assign(columnObj, singleSelectColumnObj);
    } else if (attrDataType === 'multiSelect') {
      const multiSelectColumnObj = getMultiSelectColumnObj(
        allDropdowns[attrInternalName],
        label,
        language,
      );
      Object.assign(columnObj, multiSelectColumnObj);
    } else if (attrDataType === 'number') {
      const numberColumnObj = getNumberColumnObj();
      Object.assign(columnObj, numberColumnObj);
    } else if (attrDataType === 'boolean') {
      const singleBooleanColumnObj = getBooleanColumnObj();
      Object.assign(columnObj, singleBooleanColumnObj);
    } else if (attrDataType === 'checkBox') {
      const checkBoxColumnObj = getCheckBoxColumnObj();
      Object.assign(columnObj, checkBoxColumnObj);
    } else if (attrDataType === 'price') {
      const [currencyColumn] = allColumns.filter(
        (filterItem) =>
          filterItem.attrDataType === 'currency' && filterItem.formOrderNo === column.formOrderNo,
      );
      columnObj.currency_col_internal_name = currencyColumn?.attrInternalName ?? null;
    }

    if (attrDataType === 'string') {
      columnObj.filterOperators = getGridStringOperators();
    }

    columnObj.renderEditCell = (params) => <RenderEditField {...params} />;

    return columnObj;
  });

  return { gridColumns: wrapFilterwithParam(columnsData) };
}

export const getHeaderName = (res, mandatoryColumn, language) => {
  const getName = res[`attrDisplayName${language}`] ?? res[`attrDisplayNameen`];
  const label = getName?.charAt(0).toUpperCase() + getName?.slice(1);
  const headerName = `${label}${mandatoryColumn ? '*' : ''}`;
  return { headerName, label };
};

export const getColumnObj = (column, language) => {
  const { headerName, label } = getHeaderName(column, column.attrMandatory, language);
  const isEditable = column.editable === true;

  const col = {
    minWidth: 150,
    ...column,
    headerName,
    label,
    editable: isEditable,
    flex: 1,
  };

  col.renderCell =
    col.renderCell ||
    ((params) => {
      const { row, value, colDef, field, formattedValue } = params;
      const { attrDataType: type, editable } = colDef;

      const isValidationNeeded = getValidationNeeded(row, field, value);
      const error = isValidationNeeded ? fieldValidator(colDef, value, language) : null;

      const iconVisible = editable === true;

      if (error) {
        return (
          <StyledTooltip title={error.msg}>
            <div
              className={
                error?.msg ? `${style.render_edit_cell} Mui-error` : style.render_edit_cell
              }
            >
              <div className={style.cell}>
                <div className={style.cell_content}>{formattedValue}</div>
              </div>
            </div>
          </StyledTooltip>
        );
      }

      const isValidTooltip = value && formattedValue?.length > 18;
      if (
        (type === 'textarea' && isValidTooltip) ||
        (type === 'string' && isValidTooltip) ||
        (type === 'singleSelect' && isValidTooltip)
      ) {
        return (
          <div className={style.tootip_outer}>
            <Tooltip placement='bottom' label={formattedValue}>
              <div className={style.cell}>
                <div className={style.cell_content}>{formattedValue}</div>
                {iconVisible && getIcon(type)}
              </div>
            </Tooltip>
          </div>
        );
      }

      return (
        <CellWithTooltipCheck
          formattedValue={formattedValue}
          type={type}
          getIcon={getIcon}
          iconVisible={iconVisible}
          error={error}
          value={value}
        />
      );
    });

  return col;
};

export const getNumberColumnObj = () => {
  const valueGetter = (value: number | string | null | undefined) => {
    return !value && value !== 0 ? null : +value;
  };
  const valueParser = (value: number | string | null | undefined) => {
    return !value && value !== 0 ? null : +value;
  };
  const valueFormatter = (value: number | string | null | undefined) => {
    return value || value == 0 ? value : '';
  };
  return { valueGetter, valueParser, valueFormatter, align: 'left' };
};

const getBooleanColumnObj = () => {
  const valueOptions = [
    { label: 'false', value: false },
    { label: 'true', value: true },
  ];
  const valueFormatter = (value) => {
    const selected = valueOptions.find((item) => item.value === value);
    return selected?.label ?? null;
  };
  return { valueOptions, valueFormatter };
};

const getDateColumnObj = () => {
  const valueGetter = (value) => {
    return value ?? null;
  };
  const valueFormatter = (value) => {
    return value ? `${dayjs(value).format('DD/MM/YYYY')}` : '';
  };

  const filterOperators = getGridDateOperators().map((operator) => ({
    ...operator,
    InputComponent: operator.InputComponent ? RenderHeaderDateFilterField : undefined,
  }));
  return { flex: 1, width: 250, valueGetter, valueFormatter, filterOperators };
};

const getSingleSelectColumnObj = (options) => {
  const valueOptions = options ?? [];
  const filterOperators = getGridSingleSelectOperators().map((operator) => {
    if (operator.value !== 'isAnyOf') {
      return {
        ...operator,
        getValueAsString,
        InputComponent: operator.InputComponent
          ? (props: GridFilterInputValueProps) => (
              <AutocompleteInput
                {...props}
                options={valueOptions}
                getAutocompleteOption={getAutocompleteOption}
              />
            )
          : undefined,
      };
    } else return { ...operator, getValueAsString };
  });

  // custom operators
  const getApplyFilterFn = () => {
    return (cellValue) => {
      return cellValue === null || cellValue == '';
    };
  };
  const getBlankValueAsString = () => '';
  const EmptyInputComponent = (props: {
    item: GridFilterItem;
    applyValue: (value: GridFilterItem) => void;
  }) => {
    const { item, applyValue } = props;

    React.useEffect(() => {
      applyValue({ ...item, value: 'EMPTY_VALUE' });
    }, []);

    return null;
  };
  filterOperators.push(
    {
      headerLabel: 'Empty',
      label: 'is empty',
      value: 'isEmpty',
      getApplyFilterFn,
      getValueAsString: getBlankValueAsString,
      InputComponent: EmptyInputComponent,
    },
    {
      headerLabel: 'Not Empty',
      label: 'is not empty',
      value: 'isNotEmpty',
      getApplyFilterFn,
      getValueAsString: getBlankValueAsString,
      InputComponent: EmptyInputComponent,
    },
    {
      headerLabel: 'Contains',
      label: 'contains',
      value: 'contains',
      getApplyFilterFn,
      getValueAsString: getBlankValueAsString,
      InputComponent: GridFilterInputValue,
    },
    {
      headerLabel: 'Does Not Contain',
      label: 'does not contain',
      value: 'doesNotContain',
      getApplyFilterFn,
      getValueAsString: getBlankValueAsString,
      InputComponent: GridFilterInputValue,
    },
  );

  function getAutocompleteOption(value: GridFilterItem['value']): PartialGridFilterItem {
    return valueOptions.find((option) => value === option.value) ?? value;
  }
  function getValueAsString(value: GridFilterItem['value']): string {
    if (Array.isArray(value)) {
      return value
        .map((val) => getAutocompleteOption(val)?.label)
        .filter(Boolean)
        .join(', ');
    }

    return getAutocompleteOption(value)?.label;
  }
  const getOptionLabel = (value) => {
    return value?.label;
  };
  const getOptionValue = (value) => {
    return value?.value;
  };

  const valueFormatter = (value) => {
    const option = valueOptions.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  return {
    width: 200,
    valueOptions,
    getOptionLabel,
    getOptionValue,
    filterOperators,
    valueFormatter,
  };
};

const getMultiSelectColumnObj = (options, label, language) => {
  const valueOptions = options ?? [];
  const valueGetter = (value) => {
    return value ? value : [];
  };
  const renderCell = (params) => {
    const { row, value, colDef, field } = params;
    const { valueOptions: options } = colDef;
    const valueList = options.filter((item) => value.includes(item.value));
    const tooltip = valueList?.map(({ label }) => label).join(', ');
    let error = null;
    const isValidationNeeded = getValidationNeeded(row, field, value);
    if (isValidationNeeded) {
      error = fieldValidator(colDef, value, language);
    }
    if (error) {
      return (
        <StyledTooltip title={error.msg}>
          <div
            className={error?.msg ? `${style.render_edit_cell} Mui-error` : style.render_edit_cell}
          >
            <div className={style.cell}>{params.formattedValue}</div>
          </div>
        </StyledTooltip>
      );
    }
    return (
      <div className={style.cell}>
        <Tooltip label={tooltip}>
          <div>
            {valueList.map((item) => {
              return <Chip className='mr-2' label={item.label} key={item.value} />;
            })}
          </div>
        </Tooltip>
      </div>
    );
  };
  const hasMatchingItem = (selectedValues, cellValues) => {
    if (!cellValues?.length) {
      return false;
    }

    return cellValues.some((item) => selectedValues?.some((obj) => obj.value === item));
  };

  const createApplyFilterFn = (filterItem) => {
    if (!Array.isArray(filterItem.value) || !filterItem.value.length) {
      return null;
    }

    return ({ value }) => {
      return hasMatchingItem(filterItem.value, value);
    };
  };

  const filterOperators = getGridSingleSelectOperators()
    .filter((filterItem) => filterItem.value === 'isAnyOf')
    .map((operator) => ({
      ...operator,
      getApplyFilterFn: createApplyFilterFn,
      InputComponent: operator.InputComponent ? RenderHeaderMultiSelectFilterField : undefined,
      InputComponentProps: { valueOptions, label },
    }));
  return {
    width: 200,
    valueOptions,
    valueGetter,
    renderCell,
    filterOperators,
  };
};

const getCheckBoxColumnObj = () => {
  const renderCell = (params) => {
    const { value } = params;
    const checked = value === 'S' || value === true || value === 'copy-existing';
    return <Checkbox checked={checked} readOnly />;
  };
  return {
    width: 200,
    renderCell,
  };
};

const getValidationNeeded = (row, field, value) => {
  const { backup, isCommitRow, touched, status } = row;

  if (status === 'N' || status === 'C' || status === 'E') {
    if (
      isCommitRow ||
      (status === 'E' && backup?.[field] !== value) ||
      ((status === 'N' || status === 'C') && touched[field])
    ) {
      return true;
    }
  }
};

const wrapFilterwithParam = (columnsData) => {
  const defaultColumnTypes = getGridDefaultColumnTypes();
  const wrapOperator = (operator) => {
    const getApplyFilterFn = (filterItem, column) => {
      const innerFilterFn = operator.getApplyFilterFn(filterItem, column);
      if (!innerFilterFn) {
        return innerFilterFn;
      }

      return (value, row, col, apiRef) => {
        if (row.status === 'N' || row.status === 'C' || row.status === 'E') {
          return true;
        }
        return innerFilterFn(value, row, col, apiRef);
      };
    };

    return {
      ...operator,
      getApplyFilterFn,
    };
  };

  return columnsData.map((col) => {
    let columnType = col.type ?? DEFAULT_GRID_COL_TYPE_KEY;
    if (columnType === 'textarea' || columnType === 'currency' || columnType === 'email') {
      columnType = 'string';
    } else if (columnType === 'price') {
      columnType = 'number';
    } else if (columnType === 'multiSelect') {
      columnType = 'singleSelect';
    } else if (columnType === 'checkBox') {
      columnType = 'boolean';
    }
    const filterOperators = col.filterOperators ?? defaultColumnTypes[columnType]?.filterOperators;

    return {
      ...col,
      allAccessibleColumns: columnsData,
      filterOperators: filterOperators?.map((operator) => wrapOperator(operator)),
    };
  });
};

export const getIcon = (type) => {
  if (type === 'date') {
    return (
      <IconButton variant='ghost' size='small' aria-label='Date'>
        <CalendarToday className={style.icon} />
      </IconButton>
    );
  } else if (type === 'singleSelect' || type === 'multiSelect') {
    return (
      <IconButton variant='ghost' size='small' aria-label='Date'>
        <UnfoldMore className={style.icon} />
      </IconButton>
    );
  }
  return null;
};
